import { useState, useEffect } from 'react';
import TimesheetsManager from './TimesheetsManager';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WarehouseTimesheets = () => {
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    start_date: '',
    end_date: '',
    scope: 'all',
    employee_id: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch employees');
      
      const data = await response.json();
      const warehouseEmp = data.filter(emp => emp.department === 'Warehouse');
      setEmployees(warehouseEmp);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleGenerate = async () => {
    if (!generateForm.start_date || !generateForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (generateForm.scope === 'individual' && !generateForm.employee_id) {
      toast.error('Please select an employee');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/timesheets/warehouse/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generateForm)
      });

      if (!response.ok) throw new Error('Failed to generate timesheets');

      const result = await response.json();
      toast.success(`Generated ${result.count} timesheet(s) successfully`);
      setShowGenerateDialog(false);
      
      // Refresh the page to show new timesheets
      window.location.reload();
    } catch (error) {
      console.error('Error generating timesheets:', error);
      toast.error('Failed to generate timesheets');
    } finally {
      setGenerating(false);
    }
  };

  const setDateRange = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1); // Monday
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastWeekEnd = new Date(today);
        lastWeekEnd.setDate(today.getDate() - today.getDay());
        const lastWeekStart = new Date(lastWeekEnd);
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
        start = lastWeekStart.toISOString().split('T')[0];
        end = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setGenerateForm(prev => ({ ...prev, start_date: start, end_date: end }));
  };

  return (
    <div>
      {/* Generate Button */}
      <div className="mb-4">
        <Button
          onClick={() => setShowGenerateDialog(true)}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <FileText className="w-4 h-4 mr-2" />
          Generate Timesheets
        </Button>
      </div>

      {/* Timesheets Manager */}
      <TimesheetsManager isFinance={false} />

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Generate Warehouse Timesheets</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Quick Date Range Buttons */}
            <div>
              <Label>Quick Select</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDateRange('this_week')}
                >
                  This Week
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDateRange('last_week')}
                >
                  Last Week
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDateRange('this_month')}
                >
                  This Month
                </Button>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <input
                  type="date"
                  id="start_date"
                  value={generateForm.start_date}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <input
                  type="date"
                  id="end_date"
                  value={generateForm.end_date}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Scope Selection */}
            <div>
              <Label htmlFor="scope">Scope</Label>
              <select
                id="scope"
                value={generateForm.scope}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, scope: e.target.value, employee_id: '' }))}
                className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Warehouse</option>
                <option value="individual">Individual Employee</option>
              </select>
            </div>

            {/* Employee Selection */}
            {generateForm.scope === 'individual' && (
              <div>
                <Label htmlFor="employee_id">Employee</Label>
                <select
                  id="employee_id"
                  value={generateForm.employee_id}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, employee_id: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Timesheets will be generated based on clock in/out data. 
                Breaks will be automatically applied based on shift times and can be adjusted during approval.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowGenerateDialog(false)} variant="outline">
              Cancel
            </Button>
            <Button onClick={handleGenerate} disabled={generating} className="bg-teal-600 hover:bg-teal-700">
              <Calendar className="w-4 h-4 mr-2" />
              {generating ? 'Generating...' : 'Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseTimesheets;
